import { Box, Typography, useTheme } from '@mui/material';
import { differenceInSeconds, format } from 'date-fns';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useShallow } from 'zustand/react/shallow';

import { replacePathParams } from 'utils/routes.utils';
import { hasSeatedItems } from 'utils/ticket.utils';

import { Routes } from 'constants/routes.constants';
import { useCartStore, useCartStoreActions } from 'services/stores/cart';

// Format time as mm:ss
const formatTimeLeft = (timeLeft: number) => {
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return format(new Date(0, 0, 0, 0, minutes, seconds), 'mm:ss');
};

interface Props {
  endTime: string | undefined;
}

const SeatsTimer = ({ endTime }: Props) => {
  const router = useRouter();
  const [time, setTime] = useState<number | undefined>();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const theme = useTheme();

  const { setSeatingTokenExpired, removeTicketingCartAndSeats } =
    useCartStoreActions();
  const [cartItems, seatingToken] = useCartStore(
    useShallow((state) => [state.items, state.seatingToken]),
  );
  const hasSeatedItemsCheck = hasSeatedItems(cartItems);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!!endTime) {
      const handleTimeDifference = () => {
        const diff = differenceInSeconds(endTime, new Date());
        setTime(diff >= 0 ? diff : 0);
        // Clear the timer and set the hold token as expired
        if (diff <= 0) {
          if (timer) clearInterval(timer);

          // When there are seated items, inform the user, and let them handle the situation
          if (hasSeatedItemsCheck) {
            setSeatingTokenExpired();
          } else if (!!seatingToken && !seatingToken.expirationHandled) {
            // Automatically handle it when there are no seated items in the cart
            const asPath = router.asPath;
            const pathToGoTo = replacePathParams(Routes.EventCart, {
              slug: router.query.slug as string,
            });
            setSeatingTokenExpired();
            removeTicketingCartAndSeats();
            if (asPath === pathToGoTo) {
              router.reload();
            } else {
              router.replace(pathToGoTo);
            }
          }
        }
      };
      // Ensures we don't have to wait a second before the timer is shown
      handleTimeDifference();
      timer = setInterval(handleTimeDifference, 1_000);
      return () => clearInterval(timer);
    }
  }, [
    endTime,
    hasSeatedItemsCheck,
    router,
    seatingToken,
    setSeatingTokenExpired,
    removeTicketingCartAndSeats,
  ]);

  if (time === undefined) return null;

  const color =
    time < 30 ? 'error.main' : time < 120 ? 'warning.main' : 'global01.400';

  return (
    <Box
      onClick={() =>
        enqueueSnackbar(intl.formatMessage({ id: 'seats.alert_message' }), {
          variant: 'seatsTimer',
          anchorOrigin: {
            horizontal: 'center',
            vertical: 'top',
          },
          autoHideDuration: 6000,
        })
      }
      sx={{
        px: 1,
        pt: '9px',
        pb: '7px',
        border: '1px solid',
        borderColor: color,
        color: color,
        bgcolor: 'background.default',
        borderRadius: 5,
        alignSelf: 'center',
        textAlign: 'center',
        width: '60px',
        display: 'inline-block',
        cursor: 'pointer',
        transition: theme.transitions.create('opacity'),

        '&:hover': {
          opacity: 0.8,
        },
      }}
    >
      <Typography
        variant="extraSmall"
        sx={{
          fontWeight: 800,
          color,
        }}
      >
        {formatTimeLeft(time)}
      </Typography>
    </Box>
  );
};

export default SeatsTimer;
